import type { ContentType, ModelName } from '@pigello/pigello-matrix';
import { Configs, matrixConfigs } from '@pigello/pigello-matrix';
import { fetchApi } from './api';
import { BASE_BACKEND_URL } from './constants';
import type {
  CurrentUser,
  FieldPermKey,
  UnpackedPermissions,
} from '@/providers/session-provider/types';

export async function getCurrentUser() {
  const res = await fetchApi({
    url: `${BASE_BACKEND_URL}/accounts/users/profile/`,
  });

  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      url: res.url,
    };
  }

  const data = await res.json();

  const permissions = unpackPerms(data.permissions);
  // Effective custom functionalities (e.g. the dw_aggr_* datawarehouse grants).
  // Left undefined when the backend predates the field, so callers can tell
  // "no grants" from "backend doesn't report grants".
  const customFunctionalities: string[] | undefined =
    data.permissions?.custom_functionalities ??
    data.permissions?.role?.custom_functionalities;

  // unpackPerms collapses these into canView:false, which is indistinguishable
  // from a role denial -- keep the raw list so callers can tell them apart.
  const pricingDisallowedModels: string[] =
    data.permissions?.pricing_disallowed_models ?? [];

  // Convert snake_case to camelCase -- Special case for self.data
  const dataAsCamelCase = Object.entries(data).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      acc[key.replace(/_([a-z])/g, (g) => g[1].toUpperCase())] = value;
      return acc;
    },
    {}
  ) as CurrentUser;

  return {
    ...dataAsCamelCase,
    permissions,
    customFunctionalities,
    pricingDisallowedModels,
  };
}
type ConfiguredAccessEntities = {
  [key: ContentType]: {
    affected_attributes: {
      [key: string]: {
        [key: string]: boolean;
        can_view: boolean;
        can_handle: boolean;
      };
    };
    affected_methods: {
      can_create: boolean;
      can_delete: boolean;
      can_update: boolean;
      can_view: boolean;
      [key: string]: boolean;
    };
  };
};
export type PackedPermissions = {
  all_regulated_models: ContentType[];
  pricing_disallowed_models: ContentType[];
  custom_functionalities?: string[];
  role: {
    unconfigured_models: ContentType[];
    configured_access_entities: ConfiguredAccessEntities[];
    custom_functionalities?: string[];
  };
};

function unpackPerms(permissions: PackedPermissions): UnpackedPermissions {
  const returnBoi = {} as UnpackedPermissions;
  const allRegulatedModels = new Set(permissions.all_regulated_models);
  const pricingDisallowedModels = new Set(
    permissions.pricing_disallowed_models
  );
  const unConfiguredModels = new Set(permissions.role.unconfigured_models);
  const configuredAccessEntities = unpackConfiguredAccessEntities(
    permissions.role.configured_access_entities
  );

  let modelName: ModelName;
  for (modelName in Configs) {
    const contentType = matrixConfigs[modelName]?.config?.contentType as
      | ContentType
      | undefined;

    if (contentType && pricingDisallowedModels.has(contentType)) {
      returnBoi[modelName] = {
        overall: {
          canView: false,
          canDelete: false,
          canCreate: false,
          canUpdate: false,
        },
        fields: {},
      };
      continue;
    }
    if (!contentType || !allRegulatedModels.has(contentType)) {
      returnBoi[modelName] = {
        overall: {
          canView: true,
          canDelete: true,
          canCreate: true,
          canUpdate: true,
        },
        fields: {},
      };
      continue;
    }
    if (unConfiguredModels.has(contentType)) {
      returnBoi[modelName] = {
        overall: {
          canView: false,
          canDelete: false,
          canCreate: false,
          canUpdate: false,
        },
        fields: {},
      };
      continue;
    }
    returnBoi[modelName] = configuredAccessEntities[contentType];
  }

  return returnBoi;
}

type UnpackedConfiguredAccessEntities = {
  [key: ContentType]: {
    fields: {
      [key: string]: {
        canView: boolean;
        canHandle: boolean;
        [key: FieldPermKey]: boolean;
      };
    };
    overall: {
      canView: boolean;
      canDelete: boolean;
      canCreate: boolean;
      canUpdate: boolean;
    };
  };
};
function unpackConfiguredAccessEntities(
  configured_access_entities: ConfiguredAccessEntities[]
): UnpackedConfiguredAccessEntities {
  const configuredAccessEntities = {} as UnpackedConfiguredAccessEntities;
  for (const accessCollection of configured_access_entities) {
    let contentType: ContentType;
    for (contentType in accessCollection) {
      const fields = accessCollection[contentType].affected_attributes;
      const methods = accessCollection[contentType].affected_methods;

      // the perms are any, so if we already have an existing with "true" then its true
      configuredAccessEntities[contentType] = {
        overall: {
          canView:
            configuredAccessEntities[contentType]?.overall?.canView ||
            methods.can_view,
          canDelete:
            configuredAccessEntities[contentType]?.overall?.canDelete ||
            methods.can_delete,
          canCreate:
            configuredAccessEntities[contentType]?.overall?.canCreate ||
            methods.can_create,
          canUpdate:
            configuredAccessEntities[contentType]?.overall?.canUpdate ||
            methods.can_update,
        },
        fields: configuredAccessEntities[contentType]?.fields ?? {},
      };
      for (const fieldName in fields) {
        const usedFieldName = fieldName
          .replace(/_([a-z])/g, (g) => g[1].toUpperCase())
          .replace('_', '');

        configuredAccessEntities[contentType].fields[usedFieldName] = {
          canView:
            configuredAccessEntities[contentType]?.fields?.[usedFieldName]
              ?.canView || fields[fieldName].can_view,
          canHandle:
            configuredAccessEntities[contentType]?.fields?.[usedFieldName]
              ?.canHandle || fields[fieldName].can_handle,
        };
      }
    }
  }

  return configuredAccessEntities;
}
